import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import {
	hasMergedCellSpan,
	MARKDOWN_SERIALIZABLE_NODE_TYPES,
	SUPPORTED_MARK_TYPES,
} from './markdownSupportedSchema.ts';

/**
 * Walks `adf` depth-first and returns a copy with all unsupported nodes /
 * marks removed, so the result can be safely round-tripped through our
 * markdown pipeline (CommonMark + GFM tables / task lists / strikethrough /
 * autolinks).
 *
 * Behaviour:
 *  - Unsupported node `type` → the node is **dropped entirely** (children and
 *    all). `adf-utils`'s `traverse` rebuilds the parent's `content` array
 *    around the removed entry.
 *  - `tableCell` / `tableHeader` with `colspan > 1` or `rowspan > 1` → dropped
 *    (GFM tables don't support merged cells, so dropping the whole cell is
 *    safer than silently losing the merge attribute and producing a misaligned
 *    table).
 *  - Supported node carrying unsupported `marks` → marks are filtered down to
 *    the supported set; the node itself is kept. (e.g. `text` with
 *    `[strong, underline]` becomes `text` with `[strong]`.) An empty marks
 *    array is removed entirely so we don't leave dangling `marks: []`.
 *
 * Returns:
 *  - The filtered ADF document (always a `doc` if the input was a `doc`).
 *  - `undefined` if the input was nullish or malformed (callers should fall
 *    back to whatever they had before — no replacement).
 *  - `undefined` if the entire root was dropped (shouldn't happen since `doc`
 *    is in the supported set, but kept for type safety).
 */
export const filterToMarkdownSafeAdf = (adf: unknown): ADFEntity | undefined => {
	if (adf === undefined || adf === null) {
		return undefined;
	}
	if (typeof adf !== 'object' || typeof (adf as { type?: unknown }).type !== 'string') {
		return undefined;
	}

	const result = traverse(adf as ADFEntity, {
		any: (node) => {
			// 1. Drop nodes whose type isn't in the supported set.
			if (!MARKDOWN_SERIALIZABLE_NODE_TYPES.has(node.type)) {
				return false;
			}

			// 2. Drop merged-cell table cells / headers.
			if ((node.type === 'tableCell' || node.type === 'tableHeader') && hasMergedCellSpan(node)) {
				return false;
			}

			// 3. Strip unsupported marks but keep the node.
			if (Array.isArray(node.marks) && node.marks.length > 0) {
				const filteredMarks = node.marks.filter(
					(mark) => mark && SUPPORTED_MARK_TYPES.has(mark.type),
				);
				if (filteredMarks.length === node.marks.length) {
					// Nothing to strip — keep node identity (returning the same node
					// reference signals "no change" to the traversal).
					return node;
				}
				if (filteredMarks.length === 0) {
					// Avoid leaving an empty `marks: []` on the output.
					const { marks: _marks, ...rest } = node;
					return rest as ADFEntity;
				}
				return { ...node, marks: filteredMarks };
			}

			return node;
		},
	});

	return result === false ? undefined : result;
};
